// ══════════════════════════════════════════════════
// SHARED ROW NORMALIZATION
// ══════════════════════════════════════════════════
// This is the exact same "clean up a raw row" logic that used to live only
// inside handleFile() (CSV/XLSX upload). It's pulled out here so that BOTH
// data sources — an uploaded file AND a live pull from the RADI API — end up
// producing rows with the identical shape. Every tab/chart in the app reads
// from that shape, so as long as this function's output looks the same,
// nothing else in the app needs to know or care where the data came from.
//
// `json` is a plain array of objects (one object per transaction/row),
// whatever the keys happen to be named.

// Some backends return Mongo "extended JSON" dates as { "$date": "..." }
// instead of a plain string/Date. Unwrap that here so date parsing below
// works no matter which shape we're handed.
function unwrapDate(v) {
  if (v && typeof v === "object" && "$date" in v) return v["$date"];
  return v;
}

export function normalizeRows(json) {
  const normalized = json.map(row => {
    const r = {};
    Object.entries(row).forEach(([k, v]) => { r[k.trim()] = v; });

    // --- Best-effort fallbacks for alternate/raw API field names ---
    // The CSV export uses friendly column names (e.g. "Transaction ID",
    // "Site"). The raw API may use different/raw internal field names
    // (e.g. "_id", "site" as a bare numeric ID). These fallbacks only kick
    // in if the friendly name isn't already present, so CSV parsing is
    // completely unaffected.
    r["Transaction ID"] = r["Transaction ID"] ?? r["_id"] ?? r["transactionid"] ?? "";
    // RADI's raw API gives both the bare numeric id ("company"/"site") AND a
    // human-readable name ("companyname"/"sitename") — prefer the name.
    r["Company"] = r["Company"] ?? r["companyname"] ?? r["company"] ?? "";
    r["Site"] = r["Site"] ?? r["sitename"] ?? r["site"] ?? "";
    r["Operator"] = r["Operator"] ?? r["operator"] ?? "";
    r["IMEI"] = r["IMEI"] ?? r["esnNumber"] ?? r["device_imei"] ?? r["FD_IMEI"] ?? "";
    r["Serial Number"] = r["Serial Number"] ?? r["deviceSerialNumber"] ?? r["serialnumber"] ?? "";
    r["Model Number"] = r["Model Number"] ?? r["modelnumber"] ?? "";
    r["Error Code"] = r["Error Code"] ?? r["errorCode"] ?? "";
    r["Port Number"] = r["Port Number"] ?? r["portNumber"] ?? "";
    r["Computer Name"] = r["Computer Name"] ?? r["workstationName"] ?? "";
    if (!r["Date Time"] && !r["Date"]) {
      const fallbackDate = unwrapDate(r["timeCreated"]) ?? unwrapDate(r["StartTime"]) ?? unwrapDate(r["serverTime"]);
      if (fallbackDate) r["Date Time"] = fallbackDate;
    }
    if (!r["Server Time"] && r["serverTime"]) r["Server Time"] = unwrapDate(r["serverTime"]);
    // RADI's raw API reports the whole transaction's duration as "exeT" (a
    // string, in milliseconds) — not "Total test time" like the CSV export.
    // Confirmed against the same record's time_start/time_end gap (matches
    // within a fraction of a second), so it's the same measurement, just
    // under a different, undocumented field name.
    r["Total test time"] = r["Total test time"] ?? r["exeT"] ?? "";

    const dt = r["Date"] || r["Date Time"] || "";
    let dObj = null;
    if (dt instanceof Date) dObj = dt;
    else if (typeof dt === "string" && dt) dObj = new Date(dt);
    r._date = dObj;
    // IMPORTANT: use LOCAL date parts here, not toISOString(). "Date" often
    // arrives as a plain date-only string (e.g. "08/20/2026"), which the
    // browser parses as local midnight — converting that to UTC via
    // toISOString() rolls it back to the previous day for anyone in a
    // positive UTC offset (e.g. Europe/Paris, UTC+2), so every row showed up
    // one calendar day early. Reading the local getters back out avoids that
    // round-trip entirely.
    const pad2 = n => String(n).padStart(2, "0");
    r._dateStr = dObj && !isNaN(dObj) ? `${dObj.getFullYear()}-${pad2(dObj.getMonth() + 1)}-${pad2(dObj.getDate())}` : "";
    r._year = dObj && !isNaN(dObj) ? String(dObj.getFullYear()) : "";
    r._month = dObj && !isNaN(dObj) ? `${dObj.getFullYear()}-${pad2(dObj.getMonth() + 1)}` : "";
    // RADI's raw API calls the phone model "sourceModel"/"modelCode" and the
    // maker "mnf" — NOT "sourceMake" (that field is actually a model/variant
    // string like "iPhone14,7", not a maker name).
    r["Model"] = r["Model"] || r["model"] || r["sourceModel"] || r["modelCode"] || "";
    r["Manufacturer"] = r["Manufacturer"] || r["mnf"] || r["manufacturer"] || r["prop_maker"] || "";
    r["OS"] = r["OS"] || r["os_version"] || r["iOS"] || "";
    r["App version"] = r["App version"] || r["appv"] || r["RADI app version"] || "";
    // "srcId" is RADI's actual station identifier (e.g. "radi045") — matches
    // the "Station ID" column from the portal's CSV export.
    r["Station ID"] = r["Station ID"] || r["srcId"] || r["station_id"] || r["wsid"] || "";
    // RADI's raw API reports pass/fail as "status" (e.g. "SUCCESS"/"FAILURE").
    r["Overall status"] = r["Overall status"] || r["status"] || r["APPSTATUS"] || "";
    // CSV exports embed the site name plus its numeric id, e.g. "Denver (id:53)" — strip the id for display.
    r["Site"] = String(r["Site"] || "").replace(/\(id:\d+\)/, "").trim();
    r["Profile name"] = r["Profile name"] || r["profileDesc"] || "";
    r["Operator"] = r["Operator"] || r["operator"] || r["Profile name"] || "";
    r["Souce App"] = r["Souce App"] || r["sourceapp"] || "";
    // Truncate known large columns that contain raw JSON — prevents DOM crash in Raw Data tab
    const LARGE_COLS = ["result", "logFile", "Souce App"];
    LARGE_COLS.forEach(col => { if (r[col] && String(r[col]).length > 120) r[col] = String(r[col]).slice(0, 120) + "…"; });
    return r;
  });

  // Detect tests that appear in result_FAIL but have no dedicated FT_ column
  const colsWithData = new Set();
  normalized.forEach(r => Object.entries(r).forEach(([k, v]) => { if (v !== "" && v != null) colsWithData.add(k); }));
  const resultFailTests = new Set();
  normalized.forEach(r => {
    String(r["result_FAIL"] || "").split(",").forEach(v => {
      const name = v.trim(); if (name) resultFailTests.add(name);
    });
  });
  const failOnlyTests = [...resultFailTests].filter(name => !colsWithData.has(`FT_${name}`)).sort();

  return { normalized, failOnlyTests };
}
